import { useEffect, type RefObject } from "react";

/**
 * ============================================================================
 *  usePiegeFocus.ts — Garder le focus dans un dialogue, puis le rendre
 * ----------------------------------------------------------------------------
 *  Un dialogue modal (`aria-modal`) ne doit pas laisser Tab s'échapper vers la page
 *  derrière lui : on boucle sur ses éléments focalisables. À la fermeture le focus
 *  retourne à ce qui l'a ouvert, pour qu'un visiteur au clavier retrouve sa place.
 *
 *  `depart` : l'élément à focaliser à l'ouverture (la ligne courante, par exemple) ;
 *  `origine` : celui qui a ouvert le dialogue. À défaut, c'est l'élément qui avait le
 *  focus au moment de l'ouverture.
 * ==========================================================================*/

const FOCALISABLES = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function usePiegeFocus(
  conteneur: RefObject<HTMLElement | null>,
  actif: boolean,
  depart: () => HTMLElement | null | undefined,
  origine: () => HTMLElement | null | undefined,
): void {
  useEffect(() => {
    if (!actif) return;
    const racine = conteneur.current;
    if (!racine) return;
    const precedent = origine() ?? (document.activeElement as HTMLElement | null);

    (depart() ?? racine.querySelector<HTMLElement>(FOCALISABLES))?.focus({ preventScroll: true });

    const surTouche = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const elements = [...racine.querySelectorAll<HTMLElement>(FOCALISABLES)];
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }
      const premier = elements[0]!;
      const dernier = elements[elements.length - 1]!;
      const actuel = document.activeElement as HTMLElement | null;
      // Hors du dialogue (focus perdu) ou au bout de la boucle : on revient dedans.
      if (!actuel || !racine.contains(actuel)) {
        event.preventDefault();
        (event.shiftKey ? dernier : premier).focus();
      } else if (event.shiftKey && actuel === premier) {
        event.preventDefault();
        dernier.focus();
      } else if (!event.shiftKey && actuel === dernier) {
        event.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", surTouche, true);
    return () => {
      document.removeEventListener("keydown", surTouche, true);
      // Le bouton d'origine peut avoir disparu (la bande a changé de mode) : sans lui, rien à rendre.
      if (precedent && precedent.isConnected) precedent.focus({ preventScroll: true });
    };
    // `depart` et `origine` ne sont lus qu'à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif, conteneur]);
}
